import React, { useContext, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { API, AuthContext } from '@/App';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Plus, Pencil, Power, Check, X, ShieldCheck } from 'lucide-react';

// Global-Administrator manager for the shared Primary Operational Role list.
// Operational roles ONLY — not system-access roles, capabilities or permissions.
const RoleManagerDialog = ({ open, onClose, onChanged }) => {
  const { token } = useContext(AuthContext);
  const auth = { headers: { Authorization: `Bearer ${token}` } };
  const [roles, setRoles] = useState([]);
  const [newName, setNewName] = useState('');
  const [editId, setEditId] = useState(null);
  const [editName, setEditName] = useState('');
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try { const r = await axios.get(`${API}/personnel/roles`, auth); setRoles(r.data || []); } catch (e) { /* ignore */ }
  };
  useEffect(() => { if (open) load(); }, [open]);

  const notify = () => { onChanged && onChanged(); };

  const add = async () => {
    const name = newName.trim();
    if (!name) return;
    setBusy(true);
    try {
      await axios.post(`${API}/personnel/roles`, { name }, auth);
      toast.success(`Role "${name}" added`);
      setNewName(''); await load(); notify();
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not add role'); } finally { setBusy(false); }
  };

  const rename = async (role) => {
    const name = editName.trim();
    if (!name || name === role.name) { setEditId(null); return; }
    setBusy(true);
    try {
      await axios.put(`${API}/personnel/roles/${role.id}`, { name }, auth);
      toast.success('Role renamed');
      setEditId(null); await load(); notify();
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not rename'); } finally { setBusy(false); }
  };

  const toggleActive = async (role) => {
    setBusy(true);
    try {
      await axios.put(`${API}/personnel/roles/${role.id}`, { is_active: !role.is_active }, auth);
      toast.success(role.is_active ? 'Role deactivated' : 'Role reactivated');
      await load(); notify();
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not update'); } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto" data-testid="role-manager-dialog">
        <DialogHeader>
          <DialogTitle>Manage Primary Operational Roles</DialogTitle>
          <DialogDescription className="text-xs">
            Shared, controlled job-role list for consistent titles. Roles in use can be deactivated (kept for
            history) but never deleted. This does not affect system access, capabilities or permissions.
          </DialogDescription>
        </DialogHeader>

        <div className="flex gap-2 mb-3">
          <input value={newName} onChange={(e) => setNewName(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') add(); }}
            placeholder="New role name…" data-testid="role-new-name"
            className="flex-1 rounded-md border border-gray-200 px-3 py-2 text-sm" />
          <button onClick={add} disabled={busy || !newName.trim()} data-testid="role-add-btn"
            className="inline-flex items-center gap-1 rounded-md bg-[#0E2A47] px-3 py-2 text-sm font-semibold text-white hover:bg-[#16324F] disabled:opacity-50">
            <Plus size={15} /> Add
          </button>
        </div>

        <div className="divide-y divide-gray-100 rounded-lg border border-gray-100" data-testid="role-list">
          {roles.map((r) => (
            <div key={r.id} className={`flex items-center gap-2 px-3 py-2 text-sm ${r.is_active ? '' : 'bg-slate-50'}`}
              data-testid={`role-row-${r.id}`}>
              {editId === r.id ? (
                <>
                  <input value={editName} onChange={(e) => setEditName(e.target.value)} autoFocus
                    data-testid={`role-edit-input-${r.id}`}
                    className="flex-1 rounded-md border border-gray-200 px-2 py-1 text-sm" />
                  <button onClick={() => rename(r)} className="text-[#5FA718]" data-testid={`role-save-${r.id}`}><Check size={16} /></button>
                  <button onClick={() => setEditId(null)} className="text-slate-400"><X size={16} /></button>
                </>
              ) : (
                <>
                  <span className={`flex-1 ${r.is_active ? 'text-slate-800' : 'text-slate-400 line-through'}`}>
                    {r.name}
                    {r.is_standard && <span className="ml-2 text-[10px] uppercase tracking-wide text-slate-400">standard</span>}
                    {!r.is_active && <span className="ml-2 text-[10px] uppercase tracking-wide text-slate-400">inactive</span>}
                  </span>
                  {r.assigned_count > 0 && (
                    <span className="text-[11px] text-slate-400" title="Personnel currently using this role">
                      {r.assigned_count} assigned
                    </span>
                  )}
                  <button onClick={() => { setEditId(r.id); setEditName(r.name); }} title="Rename"
                    data-testid={`role-rename-${r.id}`} className="text-slate-400 hover:text-slate-700"><Pencil size={14} /></button>
                  <button onClick={() => toggleActive(r)} title={r.is_active ? 'Deactivate' : 'Reactivate'}
                    data-testid={`role-toggle-${r.id}`}
                    className={r.is_active ? 'text-slate-400 hover:text-red-500' : 'text-[#5FA718]'}><Power size={14} /></button>
                </>
              )}
            </div>
          ))}
        </div>

        <p className="mt-3 flex items-center gap-1 text-[11px] text-slate-400">
          <ShieldCheck size={12} /> Deactivated roles stay on existing records but are hidden from new selection.
        </p>
        <DialogFooter>
          <button onClick={onClose} className="rounded-md border border-gray-200 px-4 py-2 text-sm" data-testid="role-manager-close">Done</button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default RoleManagerDialog;
